/**
 * Super detailed input debugging utility (only active in development mode) - tracks renders, keystrokes, props, state, callbacks, timings
 */

// ONLY enable debugging if ?debugInputs=true is in URL (no localStorage, no other checks)
const DEBUG_ENABLED = typeof window !== 'undefined' && 
  new URLSearchParams(window.location.search).get('debugInputs') === 'true';

// Log once at module load to confirm debugger status

// Track render counts per input ID
const renderCounts = new Map();
// Track keystroke events with full details
const keystrokeLog = [];
// Track all renders with full details
const renderLog = [];
// Track prop changes
const propChanges = new Map();
// Track callback invocations
const callbackLog = [];
// Performance timing
const performanceTimings = new Map();

// Get detailed stack trace
const getStackTrace = () => {
  const stack = new Error().stack;
  if (!stack) return { location: 'unknown', fullStack: [] };
  
  const lines = stack.split('\n').slice(2, 10); // Skip Error and getStackTrace
  const locations = lines.map(line => {
    const match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/);
    if (match) {
      return {
        function: match[1],
        file: match[2]?.replace(window.location.origin + '/', '')?.replace(/^.*\/src\//, 'src/') || match[2],
        line: match[3],
        column: match[4]
      };
    }
    return { raw: line.trim() };
  });
  
  return {
    location: locations[0]?.file || 'unknown',
  };
};

// Get component props from React DevTools (if available)
const getComponentInfo = (id) => {
  try {
    // Try to get React fiber info if DevTools is available
    const reactFiber = document.querySelector(`[id="${id}"]`)?.__reactInternalInstance ||
                       document.querySelector(`[id="${id}"]`)?.__reactFiber$;
    return reactFiber ? {
      componentName: reactFiber.type?.displayName || reactFiber.type?.name || 'Unknown',
      props: reactFiber.memoizedProps || {}
    } : null;
  } catch (e) {
    return null;
  }
};

// Detailed logging function
const logDetailed = (type, id, data) => {
  if (!DEBUG_ENABLED) return;
  
  const timestamp = Date.now();
  const count = renderCounts.get(id) || 0;
  const stackTrace = getStackTrace();
  const componentInfo = getComponentInfo(id);
  
  const logEntry = {
    type,
    id,
    timestamp,
    renderCount: count,
    stackTrace,
    componentInfo,
    ...data
  };
  
  // Immediate detailed console output
  const emoji = type === 'render' ? '🔄' : type === 'keystroke' ? '⌨️' : type === 'propChange' ? '📝' : type === 'callback' ? '🔔' : '📊';
  
  console.group(`${emoji} [${id}] ${type.toUpperCase()} #${count}`);
  console.log('📋 ID:', id);
  console.log('🔢 Render Count:', count);
  console.log('⏰ Timestamp:', new Date(timestamp).toISOString());
  
  if (componentInfo) {
    console.log('🧩 Component:', componentInfo.componentName);
    console.log('🎯 Props:', componentInfo.props);
  }
  
  // Log all data details
  Object.keys(data).forEach(key => {
    const value = data[key];
    if (value !== undefined && value !== null) {
      if (typeof value === 'object') {
        console.log(`📦 ${key}:`, JSON.stringify(value, null, 2));
      } else if (typeof value === 'function') {
        console.log(`⚙️ ${key}:`, value.toString().substring(0, 100) + '...');
      } else {
        console.log(`📌 ${key}:`, value);
      }
    }
  });
  
  // Performance info
  if (performance.now) {
    console.log('⚡ Performance.now:', performance.now().toFixed(2), 'ms');
  }
  
  console.groupEnd();
  
  // Store in logs
  if (type === 'render') {
    renderLog.push(logEntry);
    
    // Check for excessive renders PER KEYSTROKE (not total count) - only warn if multiple renders happened within 200ms (should be 1-2 max per keystroke)
    const recentRendersForThisInput = renderLog
      .filter(r => r.id === id && r.timestamp >= timestamp - 200)
      .slice(-5);
    
    // If 3+ renders in 200ms, it's excessive (should only be 1-2: local state + maybe Redux)
    if (recentRendersForThisInput.length >= 3) {
      const timeSpan = recentRendersForThisInput[recentRendersForThisInput.length - 1].timestamp - recentRendersForThisInput[0].timestamp;
      if (timeSpan < 200) {
        console.warn(`⚠️ [${id}] EXCESSIVE RENDERS: ${recentRendersForThisInput.length} renders in ${timeSpan}ms! (Expected: 1-2 per keystroke)`);
        console.table(recentRendersForThisInput.map(r => ({
          render: r.renderCount,
          trigger: r.renderTrigger || 'unknown',
          timeSinceFirst: (r.timestamp - recentRendersForThisInput[0].timestamp) + 'ms'
        })));
      }
    }
  } else if (type === 'keystroke') {
    keystrokeLog.push(logEntry);
  } else if (type === 'callback') {
    callbackLog.push(logEntry);
  }
};

export const trackInputRender = (id, value, props = {}) => {
  if (!DEBUG_ENABLED) return;
  
  // Only track meaningful renders (skip React dev mode double-renders)
  const lastRender = renderLog.length > 0 ? renderLog[renderLog.length - 1] : null;
  const isDuplicate = lastRender && 
    lastRender.id === id && 
    lastRender.value === value && 
    (Date.now() - lastRender.timestamp) < 10;
  
  if (isDuplicate) return; // Skip duplicate render tracking
  
  const count = (renderCounts.get(id) || 0) + 1;
  renderCounts.set(id, count);
  
  // Track previous props to detect changes
  const prevProps = propChanges.get(id) || {};
  const propDiff = {};
  Object.keys(props).forEach(key => {
    if (prevProps[key] !== props[key]) {
      propDiff[key] = { from: prevProps[key], to: props[key] };
    }
  });
  propChanges.set(id, { ...prevProps, ...props });
  
  // Extract render trigger information
  const renderTrigger = props.trigger || (Object.keys(propDiff).length > 0 ? propDiff : 'parent re-render or memo bypass');
  const renderNumber = props.renderNumber || count;
  
  logDetailed('render', id, {
    value: value?.substring?.(0, 50) || value,
    valueLength: typeof value === 'string' ? value.length : 0,
    renderNumber: renderNumber, // Component-level render count
    renderCount: count, // Global render count for this input ID
    props: props.allProps || props,
    propChanges: Object.keys(propDiff).length > 0 ? propDiff : 'no prop changes detected',
    renderTrigger: renderTrigger,
    memoryUsage: performance.memory ? {
      used: (performance.memory.usedJSHeapSize / 1048576).toFixed(2) + ' MB',
      total: (performance.memory.totalJSHeapSize / 1048576).toFixed(2) + ' MB',
      limit: (performance.memory.jsHeapSizeLimit / 1048576).toFixed(2) + ' MB'
    } : 'not available'
  });
};

export const trackInputKeystroke = (id, value, event = null) => {
  if (!DEBUG_ENABLED) return;
  const count = renderCounts.get(id) || 0;
  
  logDetailed('keystroke', id, {
    value: value?.substring?.(0, 50) || value,
    valueLength: typeof value === 'string' ? value.length : 0,
    previousValue: keystrokeLog.length > 0 ? keystrokeLog[keystrokeLog.length - 1]?.value : 'none',
    renderCount: count,
    timeSinceLastKeystroke: keystrokeLog.length > 0 
      ? (Date.now() - keystrokeLog[keystrokeLog.length - 1].timestamp) + ' ms'
      : 'first keystroke'
  });
  
  const keystrokeTimestamp = Date.now();
  keystrokeLog.push({ id, value, timestamp: keystrokeTimestamp });
  
  // Show cascade warning - only check renders that happened AFTER this keystroke (filter renders for this input ID that happened in the last 100ms right after keystroke)
  const recentRenders = renderLog
    .filter(r => r.id === id && r.timestamp >= keystrokeTimestamp - 100)
    .slice(-5);
  
  if (recentRenders.length >= 3) {
    const timeSpan = recentRenders[recentRenders.length - 1].timestamp - recentRenders[0].timestamp;
    // If 3+ renders happened in less than 100ms after keystroke, it's a cascade
    if (timeSpan < 100 && recentRenders[0].timestamp >= keystrokeTimestamp - 50) {
      console.error(`🚨 [${id}] RENDER CASCADE DETECTED: ${recentRenders.length} renders in ${timeSpan}ms after keystroke!`);
      console.table(recentRenders.map(r => ({
        render: r.renderCount,
        timestamp: new Date(r.timestamp).toISOString(),
        timeSinceKeystroke: (r.timestamp - keystrokeTimestamp) + 'ms',
        location: r.stackTrace.location,
        trigger: r.renderTrigger || 'unknown'
      })));
    }
  }
};

export const trackCallbackInvocation = (id, callbackName, args = {}) => {
  if (!DEBUG_ENABLED) return;
  logDetailed('callback', id, {
    callbackName,
    arguments: args,
    renderCount: renderCounts.get(id) || 0
  });
};

export const trackPropChange = (id, propName, oldValue, newValue) => {
  if (!DEBUG_ENABLED) return;
  if (oldValue === newValue) return;
  
  logDetailed('propChange', id, {
    propName,
    oldValue: typeof oldValue === 'object' ? JSON.stringify(oldValue) : oldValue,
    newValue: typeof newValue === 'object' ? JSON.stringify(newValue) : newValue,
    changed: true,
    renderCount: renderCounts.get(id) || 0
  });
};

export const getInputStats = () => {
  if (!DEBUG_ENABLED) return null;
  
  const stats = {
    renderCounts: Object.fromEntries(renderCounts),
    keystrokeCount: keystrokeLog.length,
    renderCount: renderLog.length,
    callbackCount: callbackLog.length,
    recentKeystrokes: keystrokeLog.slice(-10),
    recentRenders: renderLog.slice(-10),
    recentCallbacks: callbackLog.slice(-10),
    inputsWithExcessiveRenders: Array.from(renderCounts.entries())
      .filter(([id, count]) => count > 10)
      .map(([id, count]) => ({ id, count }))
  };
  
  console.table(stats.renderCounts);
  return stats;
};

// Export function to clear logs
export const clearDebugLogs = () => {
  renderCounts.clear();
  keystrokeLog.length = 0;
  renderLog.length = 0;
  callbackLog.length = 0;
  propChanges.clear();
  performanceTimings.clear();
  console.log('🧹 Debug logs cleared');
};

// Enable debugging: localStorage.setItem('debugInputs', 'true') Or add ?debugInputs=true to URL

